import React, { ChangeEventHandler, FC, useCallback, useEffect } from "react";
import { get } from "lodash";
import styled from "styled-components";

import { TextField } from "@formant/ui/component/common/TextField";

import { evaluateExpression } from "./evaluateExpression";
import { Label } from "./Label";
import { IInputProps, IJsonIntegerSchema, IJsonNumberSchema } from "./types";
import { updatePath } from "./updatePath";

function isNumber(value: string | number): boolean {
    return (
        value !== undefined && value !== "" && !isNaN(Number(value.toString()))
    );
}

interface INumberInputProps extends IInputProps<
    IJsonNumberSchema | IJsonIntegerSchema
> {
    integerOnly?: boolean;
}

export const NumberInput: FC<INumberInputProps> = props => {
    const { path, params, setParams, schema, integerOnly, validation } = props;

    const invalid = validation && validation?.exactPath === path.join("/");

    useEffect(() => {
        const currentValue = get(params, path);
        if (
            currentValue === undefined &&
            schema.default !== undefined &&
            isNumber(schema.default)
        ) {
            setParams(prev => updatePath(prev, path, schema.default));
        }
    }, []);

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        e => {
            const value = e.target.value.replace(" ", "");
            if (value === "") {
                setParams(prev => updatePath(prev, path, undefined));
                return;
            }

            if (!integerOnly) {
                setParams(prev => updatePath(prev, path, value));
                return;
            }

            const valueInt = parseInt(value, 10);
            setParams(prev => updatePath(prev, path, valueInt));
        },
        [path, setParams],
    );

    if (!evaluateExpression(params, path, schema["$formant.visible.when"])) {
        return null;
    }

    const getValue = () => {
        const value = get(params, path);
        if (
            value === undefined ||
            (typeof value === "string" && !isNumber(value.trim()))
        ) {
            return undefined;
        }
        return value;
    };

    return (
        <Container>
            <Label
                value={schema.title}
                description={schema.description}
                link={schema["$formant.documentationUrl"]}
                invalid={invalid}
            />
            <TextField
                small={true}
                value={getValue()}
                onChange={handleChange}
                placeholder={
                    getValue() === undefined
                        ? schema["$formant.placeholder"] ||
                          `Edit ${integerOnly ? "integer" : "number"}`
                        : undefined
                }
                errorMessage={invalid ? "required" : undefined}
                onKeyDown={event => {
                    if (
                        event.key === "Backspace" ||
                        event.key === "." ||
                        event.key === "-" ||
                        event.key.slice(-1) === "."
                    ) {
                        return;
                    }
                    if (!/[0-9.]/.test(event.key)) {
                        event.preventDefault();
                    }
                }}
            />
        </Container>
    );
};

const Container = styled.div`
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
        /* Firefox */
        input[type="number"] {
            -moz-appearance: textfield;
        }
    }
`;
